import { useCallback, useSyncExternalStore } from 'react';

/**
 * What the client has read, and what the client has written — held **on the
 * device and nowhere else**.
 *
 * ## What this is, said plainly
 *
 * There is no `recommendation` table and no `message` table. `client-coach-presence`
 * adds neither: its design says so outright under Non-Goals, and the reason is
 * the same one `client-daily-loop` gave for the session ticks — settling a shape
 * in a migration before anyone has used the screen is expensive to undo, and
 * `migrations-forward-only` means a wrong shape on `main` is corrected by a
 * second migration rather than a revert.
 *
 * So three things that look like product state are held here instead:
 *
 *   * **which notes have been opened.** The read mark belongs in the database —
 *     `recommendations`, "Read state", requires the coach to see "Read on
 *     <date>" — and until there is one, it is a fact about this browser;
 *   * **when the thread was last opened**, which is the whole of unread;
 *   * **what the client has typed into the thread**.
 *
 * **Nothing here is sent anywhere. No coach is notified. Nothing survives
 * reinstalling the app, clearing site data, or opening it on another phone.**
 * A message the client writes reaches nobody: it is drawn in the thread because
 * a composer that swallowed what was typed would be worse, and the screen says
 * out loud, permanently and under the composer, that it has not gone anywhere.
 *
 * That last sentence is the most important one in this module. The
 * something-hurt path from the check-in ends here, and **before a real client
 * uses this product that path has to reach a person** — it is on the gate in
 * `docs/runbooks/before-the-first-real-client.md` for exactly that reason.
 *
 * ## Why it is not in the fixture module
 *
 * The same reason `./progress.ts` is not: `src/shared/fixtures/queries.ts`
 * mirrors `queries.real.ts` function for function, and the real module can only
 * answer from tables that exist. A fixture `sendMessage` would accept a message
 * and drop it, which is the precise lie this slice exists not to tell. And
 * importing the fixture module from a shared one would put invented people into
 * every production bundle, which `demo-data` forbids outright.
 *
 * So this module is identical in a fixtures build and in a real one, and no
 * screen branches on the demo flag.
 *
 * ## Failing safe
 *
 * Every read and write is wrapped. `localStorage` throws in a private window
 * with site data blocked and returns text that is not ours after a version skew.
 * Both produce the empty state — nothing read, nothing written — rather than a
 * broken screen.
 */

/** A message the client typed on this device. */
export interface SentMessage {
  id: string;
  body: string;
  /** When they sent it, as an ISO instant. */
  at: string;
  /**
   * The session the message was started from, where it was started from a
   * check-in. `null` for a message written from the thread itself.
   *
   * It is a *reference*, never a description: nothing on this path asks where it
   * hurt, how much, or for how long, and there is deliberately nowhere in this
   * shape to put an answer if something did.
   */
  sessionId: string | null;
}

export interface Presence {
  /** The ids of the notes that have been opened, in no particular order. */
  readNotes: string[];
  /** When the thread was last opened, or `null` if it never has been. */
  threadReadAt: string | null;
  /** What the client has written, oldest first. */
  sent: SentMessage[];
}

const EMPTY: Presence = { readNotes: [], threadReadAt: null, sent: [] };

/**
 * One key, and a version in it.
 *
 * The `v1` is what lets a later shape be recognised and ignored rather than
 * misread — the same arrangement `./progress.ts` uses, and for the same reason.
 */
const STORAGE_KEY = 'client.coachPresence.v1';

function isSent(value: unknown): value is SentMessage {
  if (typeof value !== 'object' || value === null) return false;
  const row = value as Partial<SentMessage>;
  return (
    typeof row.id === 'string' &&
    typeof row.body === 'string' &&
    typeof row.at === 'string' &&
    (row.sessionId === null || typeof row.sessionId === 'string')
  );
}

function parse(raw: string): Presence {
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== 'object' || value === null) return EMPTY;
    const row = value as Partial<Presence>;
    return {
      readNotes: Array.isArray(row.readNotes)
        ? row.readNotes.filter((id): id is string => typeof id === 'string')
        : [],
      threadReadAt: typeof row.threadReadAt === 'string' ? row.threadReadAt : null,
      sent: Array.isArray(row.sent) ? row.sent.filter(isSent) : [],
    };
  } catch {
    return EMPTY;
  }
}

/*
 * The parsed value is cached against the exact text it was parsed from, so that
 * `useSyncExternalStore` gets a stable identity between renders. Returning a
 * fresh object each time would loop.
 */
let cachedRaw: string | null = null;
let cached: Presence = EMPTY;

function readRaw(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Everything this device remembers. Never null; an untouched device is empty. */
export function readPresence(): Presence {
  const raw = readRaw();
  if (raw === null) return EMPTY;
  if (raw !== cachedRaw) {
    cachedRaw = raw;
    cached = parse(raw);
  }
  return cached;
}

/* Subscribers, so Today and the thread never disagree about what is unread. */
const listeners = new Set<() => void>();

function write(next: Presence): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // A browser that refuses to remember shows a note as unread twice. It is not
    // a reason to fail the screen somebody is standing in front of.
  }
  cachedRaw = null;
  for (const listener of listeners) listener();
}

/**
 * Record that a note has been opened.
 *
 * Idempotent, because the note reader calls it on every mount: opening the same
 * note twice must not grow the list.
 */
export function markNoteRead(noteId: string): void {
  const current = readPresence();
  if (current.readNotes.includes(noteId)) return;
  write({ ...current, readNotes: [...current.readNotes, noteId] });
}

/** Record that the thread has been opened, which is what clears its unread. */
export function markThreadRead(at: string = new Date().toISOString()): void {
  write({ ...readPresence(), threadReadAt: at });
}

/**
 * Keep what the client wrote.
 *
 * It is kept rather than sent. See the module header: the thread says so, under
 * the composer, every time.
 */
export function sendMessage(body: string, sessionId: string | null = null): void {
  const current = readPresence();
  const message: SentMessage = {
    id: `local-${String(current.sent.length + 1)}-${String(Date.now())}`,
    body,
    at: new Date().toISOString(),
    sessionId,
  };
  write({ ...current, sent: [...current.sent, message] });
}

/**
 * Forget what the client wrote, keeping what they have read.
 *
 * This is what withdrawing the messaging consent actually does. The spec
 * requires that turning a consent off deletes the content stored under it, and
 * on this device that content is exactly the messages they typed — the read
 * marks are not messages and are not stored under that consent.
 *
 * `threadReadAt` goes too, because it is a fact about a thread that no longer
 * has a client side; what it is not is a message, so nothing is resurrected by
 * clearing it.
 */
export function forgetSentMessages(): void {
  write({ ...readPresence(), threadReadAt: null, sent: [] });
}

/** Forget everything this device remembers. Used by tests, and by nothing else. */
export function forgetPresence(): void {
  write(EMPTY);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** What this device remembers, re-rendered when it changes anywhere in this tab. */
export function usePresence(): Presence {
  const getSnapshot = useCallback(() => readPresence(), []);
  // The server snapshot is the empty one: nothing is known before the device is
  // asked, and there is no device during a prerender.
  return useSyncExternalStore(subscribe, getSnapshot, () => EMPTY);
}

/** Which notes this device has not opened. The order of `notes` is preserved. */
export function unreadNotes<T extends { id: string }>(
  notes: readonly T[],
  presence: Presence,
): T[] {
  return notes.filter((note) => !presence.readNotes.includes(note.id));
}

/**
 * Which of the coach's messages arrived after the thread was last opened.
 *
 * The client's own messages are never unread to the client, which is why this
 * takes the author into account rather than counting the thread.
 */
export function unreadMessages<T extends { author: 'coach' | 'client'; sent_at: string }>(
  messages: readonly T[],
  threadReadAt: string | null,
): T[] {
  return messages.filter(
    (message) =>
      message.author === 'coach' && (threadReadAt === null || message.sent_at > threadReadAt),
  );
}
